'use client';

import { CancelledError, QueryClient } from '@tanstack/query-core';
import {
  MutationCache,
  QueryCache,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ReactQueryStreamedHydration } from '@tanstack/react-query-next-experimental';
import { type ReactNode, useState } from 'react';

import toast from '@edgevanta/ui/lib/toast';

import env from '@/lib/env/client';

type ReactQueryProviderProps = {
  children: ReactNode;
};

// A request aborted because its query was disabled/unmounted (e.g. a dialog
// closing while its data is still loading) is not a real failure — never toast.
function isCancelledError(err: unknown): boolean {
  return (
    err instanceof CancelledError ||
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

export default function ReactQueryProvider({
  children,
}: ReactQueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            staleTime: 60 * 1000 * 30, // 30 minutes
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({
          onError: (err, query) => {
            if (isCancelledError(err)) {
              return;
            }
            if (query.state.data !== undefined) {
              toast.error((err as Error).message);
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (err, _variables, _context, mutation) => {
            if (isCancelledError(err)) {
              return;
            }
            if (mutation.options.onError) {
              return;
            }

            toast.error((err as Error).message);
          },
        }),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryStreamedHydration>{children}</ReactQueryStreamedHydration>

      {env.NEXT_PUBLIC_USE_REACT_QUERY_DEVTOOLS ? (
        <ReactQueryDevtools initialIsOpen={false} />
      ) : null}
    </QueryClientProvider>
  );
}
